import classNames from 'classnames';
import styles from './LoanSummary.module.css';
import { FormattedMessage } from 'react-intl';
import { useQuery } from '@tanstack/react-query';
import { useStateKey } from '../../../../../iwQueryClient/useStateKey';
import {
    DrawdownRepaymentSchedule,
    FormalOffer
} from '../../../MyOffers.types';
import SummaryRow from '../../../../ui/summaryRow/SummaryRow';
import LoadingCalculationValue from '../../../../multiLoanDashboard/dashboardComponents/loadingDashboardValue/LoadingDashboardValue';
import messages from './LoanSummary.messages';
import { VariableRate } from '../../../../ui/rate';

import {
    getInterestOnlySchedule,
    getNonInterestOnlyPaymentAmount,
    isInterestOnly
} from '../../../MyOffers.helpers';
import { MoneyWithDecimals } from '../../../../ui/moneyWithDecimals';
import WithdrawalFee from '../withdrawalFee/WithdrawalFee';
import { Button, Icon, Link, Modal } from '@iwoca/orion';
import { getMonthsfromDays } from '../../../../multiLoanDashboard/accountsSelector';
import { calculateDrawdownTotalInterest } from '@api/repaymentScheduleSelector';
import { useState } from 'react';
import RepaymentScheduleModal from '../../repaymentScheduleModal/RepaymentScheduleModal';
import { InterestOnlyPaymentsAccordion } from '../interestOnly/InterestOnlyPaymentsAccordion';

type LoanSummaryProps = {
    drawdownAmount: number;
    selectedOffer: FormalOffer | null;
    showNewUI: boolean;
};

export const LoanSummary = ({
    drawdownAmount,
    selectedOffer,
    showNewUI
}: LoanSummaryProps) => {
    const stateKey = useStateKey();

    const [isModalOpen, setIsModalOpen] = useState(false);

    const handleModalClose = () => {
        setIsModalOpen(!isModalOpen);
    };

    const { isLoading, data: drawdownSchedule } = useQuery({
        queryKey: [
            'draw_down_repayment_schedule',
            stateKey,
            drawdownAmount,
            selectedOffer?.offer_id,
            selectedOffer?.product_type
        ],
        queryFn: async () => {
            // eslint-disable-next-line pandora/prefer-lapi-client
            const response = await fetch(
                `/api/lending/edge/draw_down_repayment_schedule/${stateKey}/${selectedOffer?.product_type}/${selectedOffer?.offer_id}/?amount=${drawdownAmount}`
            );

            return response.json();
        },
        enabled: Boolean(selectedOffer) && Boolean(drawdownAmount)
    });

    const greyedTextClassName = !selectedOffer && styles.greyText;

    let loanDuration = 12;
    let withdrawalFee = 0;
    let interestRate = 0;

    if (selectedOffer) {
        loanDuration =
            selectedOffer.duration!.unit === 'days'
                ? getMonthsfromDays(selectedOffer.duration!.amount)
                : selectedOffer.duration!.amount;
        withdrawalFee = selectedOffer.withdrawal_fee_percentage || 0;
        interestRate = selectedOffer.interest_rates![0].rate!;
    }

    let repaymentSchedule: DrawdownRepaymentSchedule['schedule'] | null = null;
    let scheduleSummary: DrawdownRepaymentSchedule['summary'];
    let equalMonthlyAmount = 0;
    let totalInterest = 0;
    let totalBalance = 0;
    if (drawdownSchedule?.data) {
        repaymentSchedule = drawdownSchedule.data.schedule;
        scheduleSummary = drawdownSchedule.data.summary;
        // We assume that the !is_equal_repayment means the schedule is for interest-only
        equalMonthlyAmount = scheduleSummary.is_equal_repayment
            ? scheduleSummary.simplified_schedule!.equal_repayments!.amount
            : getNonInterestOnlyPaymentAmount(repaymentSchedule!);
        totalInterest = calculateDrawdownTotalInterest(repaymentSchedule!);
        totalBalance = scheduleSummary!.total_payable!;
    }

    const borderClassName = showNewUI ? null : styles.loanSummaryBorder;

    return (
        <div className={classNames(styles.loanSummary, borderClassName)}>
            <div
                className={classNames(
                    styles.loanSummaryTitle,
                    greyedTextClassName
                )}
                data-testid="loan-summary-title"
            >
                <FormattedMessage
                    {...messages.repaymentsOverMonths}
                    values={{ months: loanDuration }}
                />

                {selectedOffer && isInterestOnly(selectedOffer) && (
                    <div className={styles.loanSummarySubtitle}>
                        <FormattedMessage {...messages.interestOnlySubtitle} />
                    </div>
                )}
            </div>
            <SummaryRow
                data-testid="interest-per-30-days"
                className={classNames(greyedTextClassName)}
            >
                <FormattedMessage {...messages.interestRateLabel} />
                <LoadingCalculationValue isLoading={isLoading}>
                    <>
                        <span className={styles.subText}>
                            <FormattedMessage
                                {...messages.interestRatePeriod}
                            />
                        </span>

                        <VariableRate
                            minimumFractionDigits={2}
                            maximumFractionDigits={2}
                            value={interestRate}
                        />
                    </>
                </LoadingCalculationValue>
            </SummaryRow>

            {selectedOffer && isInterestOnly(selectedOffer) && (
                <InterestOnlyPaymentsAccordion
                    interestOnlyPeriod={selectedOffer.interest_only_repayments!}
                    interestOnlySchedule={getInterestOnlySchedule(
                        repaymentSchedule
                    )}
                    showNewUi={showNewUI}
                />
            )}
            <WithdrawalFee
                withdrawalFee={withdrawalFee}
                className={classNames(greyedTextClassName)}
            />
            <SummaryRow
                data-testid="minimum-monthly-repayments"
                className={classNames(greyedTextClassName)}
            >
                <span className={showNewUI ? styles.wrapText : undefined}>
                    <FormattedMessage
                        {...(selectedOffer && isInterestOnly(selectedOffer)
                            ? messages.interestOnlyMonthlyRepaymentLabel
                            : messages.monthlyRepaymentLabel)}
                    />
                </span>
                <LoadingCalculationValue isLoading={isLoading}>
                    <MoneyWithDecimals
                        className={styles.summaryAmount}
                        value={equalMonthlyAmount}
                    />
                </LoadingCalculationValue>
            </SummaryRow>

            <SummaryRow
                data-testid="total-interest"
                className={classNames(greyedTextClassName)}
            >
                <FormattedMessage {...messages.totalInterestLabel} />
                <LoadingCalculationValue isLoading={isLoading}>
                    <MoneyWithDecimals
                        className={styles.summaryAmount}
                        value={totalInterest}
                    />
                </LoadingCalculationValue>
            </SummaryRow>
            <SummaryRow
                data-testid="total-repayment-amount"
                className={classNames(greyedTextClassName)}
            >
                <FormattedMessage {...messages.totalRepaymentLabel} />
                <LoadingCalculationValue isLoading={isLoading}>
                    <MoneyWithDecimals
                        className={styles.summaryAmount}
                        value={totalBalance}
                    />
                </LoadingCalculationValue>
            </SummaryRow>

            {showNewUI ? (
                <Link
                    className={styles.showScheduleLink}
                    onClick={() => {
                        setIsModalOpen(true);
                    }}
                >
                    <Icon icon="calendarOutline" />
                    <FormattedMessage {...messages.myRepaymentSchedule} />
                    <Icon icon="angleRight" />
                </Link>
            ) : (
                <Button
                    type="button"
                    variant="secondary"
                    className={styles.showSchedule}
                    disabled={Boolean(!selectedOffer)}
                    onClick={() => {
                        setIsModalOpen(true);
                    }}
                >
                    <Icon icon="calendarOutline" />
                    <FormattedMessage {...messages.myRepaymentSchedule} />
                </Button>
            )}

            <Modal isOpen={isModalOpen} onClose={handleModalClose} closeIcon>
                <RepaymentScheduleModal
                    offerId={selectedOffer?.offer_id!}
                    drawdownAmount={drawdownAmount}
                />
            </Modal>
        </div>
    );
};
